/**
 * sceneLighting.js
 *
 * Shared Three.js scene setup utilities for the design studio.
 * Provides consistent lighting, wall backdrop, shadow configuration,
 * and telephoto perspective camera across single-frame and multi-frame views.
 */

import * as THREE from 'three'

// ---------------------------------------------------------------------------
// Shadow-enabled renderer
// ---------------------------------------------------------------------------

/**
 * Configure a WebGLRenderer for soft shadow mapping.
 * Call once after creating the renderer.
 * @param {THREE.WebGLRenderer} renderer
 */
export function setupShadowRenderer(renderer) {
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = THREE.PCFSoftShadowMap
}

// ---------------------------------------------------------------------------
// Wall
// ---------------------------------------------------------------------------

/** Gallery wall dimensions (inches). 12 ft × 8 ft. */
export const WALL_WIDTH = 12 * 12   // 144
export const WALL_HEIGHT = 8 * 12   // 96
/** Frame center height for eye-level hanging (inches from floor). */
export const EYE_LEVEL = 57
/** Ceiling height - lights positioned near here (inches). */
export const CEILING_HEIGHT = WALL_HEIGHT - 4   // 92

/**
 * Create a wall plane to sit behind framed artwork.
 * For gallery layout: use WALL_WIDTH × WALL_HEIGHT, position at (0, wallCenterY, -zOffset).
 *
 * @param {number} width  - wall width in scene units (inches)
 * @param {number} height - wall height in scene units (inches)
 * @param {number|string} color - wall color (hex int or CSS string), default white
 * @param {number} zOffset - how far behind z=0 the wall sits (inches, default 4)
 * @param {number} [centerY] - wall center Y (default 0). Use WALL_HEIGHT/2 for floor-to-ceiling (bottom at y=0).
 * @returns {THREE.Mesh}
 */
export function createWallPlane(width, height, color = 0xffffff, zOffset = 4, centerY = 0) {
  const geo = new THREE.PlaneGeometry(width, height)
  const mat = new THREE.MeshStandardMaterial({
    color: typeof color === 'string' ? new THREE.Color(color) : color,
    roughness: 0.85,
    metalness: 0,
    side: THREE.FrontSide,
  })
  const mesh = new THREE.Mesh(geo, mat)
  mesh.position.set(0, centerY, -zOffset)
  mesh.receiveShadow = true
  mesh.userData = { type: 'wall' }
  return mesh
}

// ---------------------------------------------------------------------------
// Lighting
// ---------------------------------------------------------------------------

/**
 * Create soft ambient fill using a HemisphereLight.
 * Sky = white, ground = warm gray for subtle color variation.
 *
 * @param {number} intensity - light intensity (default 0.5)
 * @returns {THREE.HemisphereLight}
 */
export function createSceneAmbient(intensity = 0.5) {
  return new THREE.HemisphereLight(0xffffff, 0xe8e0d8, intensity)
}

/**
 * Create a subtle directional fill light from upper-left.
 * Does not cast shadows (avoids double-shadow artifacts).
 *
 * @param {number} intensity - light intensity (default 0.15)
 * @returns {THREE.DirectionalLight}
 */
export function createSceneFillLight(intensity = 0.15) {
  const light = new THREE.DirectionalLight(0xffffff, intensity)
  light.position.set(-20, 30, 40)
  light.castShadow = false
  return light
}

/**
 * Create a directional key light that casts shadows onto the wall.
 * Orthographic shadow camera ensures full coverage of frame + wall.
 *
 * @param {number} intensity - light intensity (default 0.4)
 * @param {number} size - half-extent of orthographic shadow frustum (default 80)
 * @returns {THREE.DirectionalLight}
 */
export function createSceneKeyLight(intensity = 0.4, size = 100, ceilingHeight = 92, xOffset = 35) {
  const light = new THREE.DirectionalLight(0xffffff, intensity)
  light.position.set(xOffset, ceilingHeight, 50)
  light.castShadow = true
  light.shadow.mapSize.set(2048, 2048)
  light.shadow.radius = 2
  light.shadow.bias = -0.0001
  light.shadow.normalBias = 0.02
  light.shadow.camera.left = -size
  light.shadow.camera.right = size
  light.shadow.camera.top = size
  light.shadow.camera.bottom = -size
  light.shadow.camera.near = 1
  light.shadow.camera.far = 120
  light.shadow.camera.updateProjectionMatrix()
  return light
}

/**
 * Create a SpotLight aimed at a specific artwork position.
 * Configured for soft directional shadows cast onto the wall.
 *
 * @param {object} opts
 * @param {THREE.Vector3|{x:number, y:number, z:number}} opts.position - light position
 * @param {THREE.Vector3|{x:number, y:number, z:number}} opts.target   - point light aims at
 * @param {number} opts.intensity  - light intensity (default 0.6)
 * @param {number} opts.angle      - spotlight cone half-angle in radians (default 0.4)
 * @param {number} opts.penumbra   - edge softness 0-1 (default 0.8)
 * @param {number} opts.shadowMapSize - shadow map resolution (default 1024)
 * @returns {THREE.SpotLight}
 */
export function createFrameSpotlight({
  position = { x: 8, y: 16, z: 30 },
  target = { x: 0, y: 0, z: 0 },
  intensity = 0.6,
  angle = 0.4,
  penumbra = 0.8,
  shadowMapSize = 1024,
} = {}) {
  const light = new THREE.SpotLight(0xffffff, intensity, 0, angle, penumbra)
  light.position.set(position.x, position.y, position.z)

  // Shadow configuration
  light.castShadow = true
  light.shadow.mapSize.set(shadowMapSize, shadowMapSize)
  light.shadow.radius = 4
  light.shadow.bias = -0.0005
  light.shadow.normalBias = 0.02
  light.shadow.camera.near = 1
  light.shadow.camera.far = 200

  // Target object (Three.js SpotLight needs a target in the scene)
  light.target.position.set(target.x, target.y, target.z)

  return light
}

/**
 * Create the full lighting setup for a single frame.
 * Returns an array of lights to add to the scene.
 *
 * @param {object} opts
 * @param {number} opts.centerX       - horizontal center of the frame (default 0)
 * @param {number} opts.centerY       - vertical center of the frame (default 0)
 * @param {number} opts.ambientIntensity  - hemisphere light intensity multiplier
 * @param {number} opts.spotIntensity     - per-frame spotlight intensity multiplier
 * @param {number} opts.fillIntensity     - fill light intensity multiplier
 * @returns {THREE.Light[]}
 */
export function createSingleFrameLighting({
  centerX = 0,
  centerY = 0,
  ambientIntensity = 0.5,
  spotIntensity = 0.6,
  fillIntensity = 0.15,
  keyLightIntensity = 0.4,
  ceilingHeight = CEILING_HEIGHT,
} = {}) {
  const lights = []

  lights.push(createSceneAmbient(ambientIntensity))
  lights.push(createSceneFillLight(fillIntensity))

  // Directional key light from ceiling: same offset as spot for consistent shadow
  const vertOffset = ceilingHeight - centerY
  const keyLight = createSceneKeyLight(keyLightIntensity, 100, ceilingHeight, vertOffset)
  keyLight.target.position.set(centerX, centerY, 0)
  lights.push(keyLight)
  lights.push(keyLight.target)

  // Spot at ceiling, offset so left and bottom shadow on frame are equal
  const spot = createFrameSpotlight({
    position: { x: centerX + vertOffset, y: ceilingHeight, z: 30 },
    target: { x: centerX, y: centerY, z: 0 },
    intensity: spotIntensity,
  })
  lights.push(spot)
  lights.push(spot.target)

  return lights
}

/**
 * Create the scene-level lighting for a multi-frame gallery view.
 * Returns ambient + fill + directional key light (with shadow camera).
 * Add per-frame spotlights separately via createFrameSpotlight().
 *
 * @param {object} opts
 * @param {number} opts.ambientIntensity
 * @param {number} opts.fillIntensity
 * @param {number} opts.keyIntensity
 * @returns {THREE.Light[]}
 */
export function createGallerySceneLighting({
  ambientIntensity = 0.5,
  fillIntensity = 0.15,
  keyIntensity = 0.4,
} = {}) {
  const lights = []
  lights.push(createSceneAmbient(ambientIntensity))
  lights.push(createSceneFillLight(fillIntensity))
  const key = createSceneKeyLight(keyIntensity, 100, CEILING_HEIGHT)
  lights.push(key)
  lights.push(key.target)
  return lights
}

// ---------------------------------------------------------------------------
// Camera
// ---------------------------------------------------------------------------

/**
 * Create a PerspectiveCamera with telephoto-style narrow FOV.
 * Positioned far from the scene to give flat, compressed perspective
 * similar to product photography with a long lens.
 *
 * @param {number} containerWidth  - viewport width in pixels
 * @param {number} containerHeight - viewport height in pixels
 * @param {number} fov - vertical field of view in degrees (default 18)
 * @returns {THREE.PerspectiveCamera}
 */
export function createTelephotoCamera(containerWidth, containerHeight, fov = 18) {
  const aspect = containerWidth / containerHeight
  const camera = new THREE.PerspectiveCamera(fov, aspect, 0.1, 2000)
  camera.position.set(0, EYE_LEVEL, 100)
  camera.lookAt(0, EYE_LEVEL, 0)
  return camera
}

/**
 * Compute the camera Z distance needed to fit a scene of given dimensions
 * within a perspective camera's view.
 *
 * @param {number} sceneWidth  - scene extent width (scene units)
 * @param {number} sceneHeight - scene extent height (scene units)
 * @param {number} fov         - camera vertical FOV in degrees
 * @param {number} aspect      - camera aspect ratio (width/height)
 * @param {number} padding     - multiplier for breathing room (default 1.05)
 * @returns {number} Z distance from origin
 */
export function computeFitDistance(sceneWidth, sceneHeight, fov, aspect, padding = 1.05) {
  const fovRad = THREE.MathUtils.degToRad(fov)
  const halfH = (sceneHeight * padding) / 2
  const halfW = (sceneWidth * padding) / 2

  // Distance needed to fit height
  const distH = halfH / Math.tan(fovRad / 2)
  // Distance needed to fit width
  const distW = halfW / (Math.tan(fovRad / 2) * aspect)

  return Math.max(distH, distW)
}
